import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Pagination,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CustomInstructionBox from "../../../../../customComponents/CustomInstructionBox";
import CustomInfoCard from "../../../../../customComponents/CustomInfoCard";
import QuizIcon from "../../../../../assets/icons/quiz-icon.svg";
import React from "react";
import { useNavigate } from "react-router-dom";
import CustomResultModal from "../../../../../customComponents/CustomResultModal";
import {
  useGetSchoolQuizzesQuery,
  useGetQuizResultQuery,
  useMarkAnswersViewedMutation,
} from "../../../../../app/quizApiSlice";
import { parseUTC } from "../../../../../helpers/dateUtils";

const ClassQuiz = () => {
  const [sortBy, setSortBy] = React.useState("date");
  const [filterSubject, setFilterSubject] = React.useState("");
  const [openModals, setOpenModals] = React.useState({});
  const navigate = useNavigate();
  const { data, isLoading } = useGetSchoolQuizzesQuery();
  const [selectedQuizId, setSelectedQuizId] = React.useState(null);
  const [correctQuiz, setCorrectQuiz] = React.useState(false);
  const { data: quizResult, isError } = useGetQuizResultQuery(
    { quiz_id: selectedQuizId || 0 },
    { skip: !selectedQuizId }
  );
  const [markAnswersViewed] = useMarkAnswersViewedMutation();

  // Warning dialog state: holds the quiz object whose answer sheet the student wants to open
  const [warnQuiz, setWarnQuiz] = React.useState(null);
  const [viewingAnswers, setViewingAnswers] = React.useState(false);

  const [page, setPage] = React.useState(1);
  const itemsPerPage = 10;

  const formatDate = (dateString) => {
    const date = parseUTC(dateString);
    if (!date) {
      throw new Error("Invalid date string");
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const subjectOptions = React.useMemo(() => {
    if (!data?.quizzes) return [];
    return [...new Set(data.quizzes.filter((q) => q.school_id).map((q) => q.subject).filter(Boolean))];
  }, [data?.quizzes]);

  const sortedQuizzes = React.useMemo(() => {
    if (!data?.quizzes) return [];
    const schoolQuizzes = data.quizzes.filter((q) => q.school_id);
    const filtered = filterSubject
      ? schoolQuizzes.filter((q) => q.subject === filterSubject)
      : schoolQuizzes;
    return [...filtered].sort((a, b) => {
      if (sortBy === "date") {
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      } else if (sortBy === "subject") {
        return a.subject.localeCompare(b.subject);
      }
      return 0;
    });
  }, [data?.quizzes, sortBy, filterSubject]);

  const paginatedQuizzes = React.useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedQuizzes.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedQuizzes, page]);

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeSort = (event) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  const handleChangeFilter = (event) => {
    setFilterSubject(event.target.value);
    setPage(1);
  };

  const totalQuizzes = sortedQuizzes.length;
  const startIndex = (page - 1) * itemsPerPage + 1;
  const endIndex = Math.min(page * itemsPerPage, totalQuizzes);

  const handleOpenModal = (quizId) => {
    setSelectedQuizId(quizId);
    setOpenModals((prev) => ({ ...prev, [quizId]: true }));
    if (quizResult) {
      const quizIds = quizResult.answers?.map((answer) => answer.quiz_id) || [];
      setCorrectQuiz(quizIds.includes(quizId));
    }
  };

  const handleCloseModal = (quizId) => {
    setOpenModals((prev) => ({ ...prev, [quizId]: false }));
    setSelectedQuizId(null);
  };

  React.useEffect(() => {
    if (quizResult && selectedQuizId !== null) {
      const quizIds = quizResult.answers?.map((answer) => answer.quiz_id) || [];
      setCorrectQuiz(quizIds.includes(selectedQuizId));
    }
  }, [quizResult, selectedQuizId]);

  const result = { correct: 0, wrong: 0 };
  quizResult?.answers?.forEach((answer) => {
    const correctOption = answer.question.options.find(
      (option) => option.id === answer.option_id && option.is_answer === true
    );
    if (correctOption) result.correct += 1;
    else result.wrong += 1;
  });

  const totalQuestions = quizResult?.answers?.length || 0;
  const percentage = (result.correct / totalQuestions) * 100;

  // Derive retake eligibility for the currently selected quiz (used by modal)
  const selectedQuiz = selectedQuizId
    ? sortedQuizzes.find((q) => q.id === selectedQuizId)
    : null;
  const selectedRetakeInfo = selectedQuiz?.retake_info ?? {};
  const selectedRetakesRemaining = selectedRetakeInfo.retakes_remaining ?? 0;
  const selectedAnswersViewed = selectedRetakeInfo.answers_viewed ?? false;
  const selectedIsAllCorrect = totalQuestions > 0 && result.correct === totalQuestions;
  const selectedCanRetake = selectedRetakesRemaining > 0 && !selectedAnswersViewed && !selectedIsAllCorrect;

  /**
   * Navigate to the full answer/solutions sheet.
   * If the student still has retakes available we must warn them first —
   * opening the sheet will exhaust their remaining retakes.
   */
  const handleViewSolutions = (quiz) => {
    const ri = quiz?.retake_info ?? {};
    const remaining = ri.retakes_remaining ?? 0;
    const viewed = ri.answers_viewed ?? false;
    const hasAnswers = Array.isArray(quiz?.answers) && quiz.answers.length > 0;

    if (hasAnswers && remaining > 0 && !viewed) {
      // Show warning — navigating away from modal first
      if (selectedQuizId) handleCloseModal(selectedQuizId);
      setWarnQuiz(quiz);
    } else {
      navigate(`view/${quiz.id}`, { state: { quiz } });
    }
  };

  const handleRetakeFromModal = (quiz) => {
    if (selectedQuizId) handleCloseModal(selectedQuizId);
    navigate(`take/${quiz.id}`, {
      state: { quiz, returnPath: "/lms/student/class-quiz" },
    });
  };

  const handleConfirmViewAnswers = async () => {
    if (!warnQuiz) return;
    setViewingAnswers(true);
    try {
      await markAnswersViewed(warnQuiz.id).unwrap();
    } catch {
      // Non-critical — proceed regardless
    } finally {
      setViewingAnswers(false);
      const quiz = warnQuiz;
      setWarnQuiz(null);
      navigate(`view/${quiz.id}`, { state: { quiz } });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <CustomInstructionBox
          heading={"Instructions"}
          textOne={"Taking Quizzes from school will affect your points"}
          textTwo={"1 point is awarded for every right answer"}
          textThree={"0.5 points are deducted for every wrong answer"}
        />
      </Box>
      <Box sx={{ marginTop: "2em" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1em",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontSize: { xs: "1rem", md: "1.25rem" },
              fontWeight: 700,
              letterSpacing: "-1px",
            }}
          >
            Class Quizzes
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              value={filterSubject}
              onChange={handleChangeFilter}
              displayEmpty
              sx={{ minWidth: 140 }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">All Subjects</MenuItem>
              {subjectOptions.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <Select value={sortBy} onChange={handleChangeSort} size="small">
              <MenuItem value="date">
                Sort by: <strong>Date</strong>
              </MenuItem>
              <MenuItem value="subject">
                Sort by: <strong>Subject</strong>
              </MenuItem>
            </Select>
          </Box>
        </Box>

        {isLoading && <LinearProgress />}

        {paginatedQuizzes.map((quiz, i) => {
          const isExpired = quiz.expires_at && parseUTC(quiz.expires_at) < new Date();
          const ri = quiz.retake_info ?? {};
          const retakesRemaining = ri.retakes_remaining ?? 0;
          const answersViewed = ri.answers_viewed ?? false;
          const quizAnswerCount = quiz.answers?.length ?? 0;
          const quizCorrectCount = quiz.correct_count ?? 0;
          const isAllCorrect = quizAnswerCount > 0 && quizCorrectCount === quizAnswerCount;
          const canRetake = quiz.answers && retakesRemaining > 0 && !answersViewed && !isExpired && !isAllCorrect;

          return (
            <Box key={quiz.id}>
              <CustomInfoCard
                title={quiz.subject}
                date={formatDate(quiz.updated_at)}
                details={quiz.questions[0]?.topic}
                handleClick={() => {
                  if (isExpired && !quiz.answers) return;
                  if (quiz.answers) {
                    handleOpenModal(quiz.id);
                  } else if (!isExpired) {
                    navigate(`take/${quiz.id}`, {
                      state: { quiz, returnPath: "/lms/student/class-quiz" },
                    });
                  }
                }}
                icon={QuizIcon}
                action={quiz.answers ? "View Result" : isExpired ? "Expired" : "Take Quiz"}
              />
              {/* Retake button rendered below the card when eligible */}
              {canRetake && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5, mb: 1, pr: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() =>
                      navigate(`take/${quiz.id}`, {
                        state: { quiz, returnPath: "/lms/student/class-quiz" },
                      })
                    }
                    sx={{ fontSize: "0.75rem", textTransform: "none" }}
                  >
                    Retake ({retakesRemaining} attempt{retakesRemaining !== 1 ? "s" : ""} left)
                  </Button>
                </Box>
              )}
              <CustomResultModal
                key={i}
                open={openModals[quiz.id] || false}
                handleClose={() => handleCloseModal(quiz.id)}
                score={percentage.toFixed(1)}
                totalQuestions={totalQuestions}
                correctAnswers={result.correct}
                wrongAnswers={result.wrong}
                handleViewSolutions={() => handleViewSolutions(selectedQuiz ?? quiz)}
                isLoading={!correctQuiz && !isError}
                isError={isError}
                retakesRemaining={selectedCanRetake ? selectedRetakesRemaining : 0}
                answersViewed={selectedAnswersViewed}
                onRetake={() => handleRetakeFromModal(selectedQuiz ?? quiz)}
              />
            </Box>
          );
        })}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "2em",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex} to {endIndex} of {totalQuizzes} entries
          </Typography>
          <Pagination
            count={Math.ceil(sortedQuizzes.length / itemsPerPage)}
            page={page}
            onChange={handleChangePage}
            color="primary"
            shape="rounded"
            sx={{ color: (theme) => theme.palette.mode === "dark" ? "#888" : "#CCCCCC" }}
          />
        </Box>
      </Box>

      {/* Warning dialog: shown before the student opens the full answer sheet when retakes remain */}
      <Dialog
        open={!!warnQuiz}
        onClose={() => setWarnQuiz(null)}
        aria-labelledby="retake-warn-title"
      >
        <DialogTitle id="retake-warn-title">Open Answer Sheet?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Viewing the full answer sheet will use up your remaining retake
            {((warnQuiz?.retake_info?.retakes_remaining ?? 0) !== 1) ? "s" : ""} for this quiz.
            You will no longer be able to retake it after viewing.
          </DialogContentText>
          <DialogContentText sx={{ mt: 1, fontWeight: 600 }}>
            Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWarnQuiz(null)} disabled={viewingAnswers}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmViewAnswers}
            color="error"
            variant="contained"
            disabled={viewingAnswers}
          >
            {viewingAnswers ? "Opening…" : "View Anyway"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassQuiz;

